// Whether a lorry may leave the plant, and what has to happen first if it may not.
//
// The thing that actually goes missing at a gate out is a portable tracker. A lorry that carries its
// own unit drives away with it and nobody has to think; a lorry that was lent one of the plant's
// units has to hand it back, and if it does not, the unit leaves on a lorry that is not coming back.
// Replacing them is expensive and the plant does not find out for days, by which time nobody
// remembers which truck.
//
// Two things make this less simple than "has the box come back", and both are the product being
// right rather than in the way:
//
//   A load going out to a customer is still being tracked. Its borrowed unit is *supposed* to leave
//   on the lorry and come back after the delivery. Red-flagging that at the plant exit would hold up
//   a lorry that owes the plant nothing today, which is its own kind of failure - a false red costs
//   a driver an argument at the barrier and teaches the gate to ignore the screen.
//
//   A unit cannot be handed back while the trip is still running: the rules will not detach the only
//   tracker from an open trip, and will not record a return before a detach. That is correct - a trip
//   in progress has to keep something reporting - but it means the order at the barrier is fixed.
//   The gate out is recorded first, which ends the trip and with it the assignment, and the unit
//   is collected second. So the desk warns before and collects after, rather than pretending it
//   can do both in one press.

/** The portable units issued to this lorry that have not come back over the counter. */
export const unreturnedDevices = (trip, data) => {
  if (!trip) return [];
  const sources = data?.sources || [];
  return (data?.assignments || [])
    .filter((a) => a.vehicleId === trip.vehicleId && a.mode === 'TEMPORARY' && !a.returnedAt)
    .map((a) => ({
      assignmentId: a.id,
      sourceId: a.sourceId,
      // Named as the counter knows it: "device 41" is not something anybody can go and find.
      identifier: sources.find((s) => s.id === a.sourceId)?.identifier || `Device ${a.sourceId}`,
      // Ended means the trip has finished with it, which is the moment it can be booked back in.
      collectable: !!a.endedAt,
    }));
};

/** The gate-out stage on this trip's route, if its route has one. */
export const exitStage = (trip) => (trip?.checkpoints || []).find((c) => c.type === 'GATE_OUT') || null;

/** Whether the gate out is the stage this trip is actually due at. */
export const exitIsNext = (trip) => {
  const stage = exitStage(trip);
  const next = (trip?.checkpoints || [])[trip?.progress];
  return !!stage && !!next && stage.id === next.id;
};

/**
 * Whether this lorry's journey finishes at the plant gate.
 *
 * It does not if the route carries on to a customer: the lorry is leaving the plant but not leaving
 * the plant's sight, and whatever is tracking it has to go with it.
 */
export const journeyEndsHere = (trip) => {
  const stages = trip?.checkpoints || [];
  const exit = stages.findIndex((c) => c.type === 'GATE_OUT');
  if (exit < 0) return false;
  return !stages.slice(exit + 1).some((c) => c.type === 'CLIENT');
};

/**
 * What the barrier should say.
 *
 *   clear    - nothing of the plant's is on the lorry.
 *   travels  - a borrowed unit goes with it, on purpose, and is due back after the delivery.
 *   warn     - the unit has to come off, and the gate out has to be recorded before it can be.
 *   collect  - the gate out is done; collect the unit now, before the lorry goes.
 *   unknown  - no open trip answers to that number.
 */
export const clearance = (trip, data) => {
  if (!trip) {
    return {
      state: 'unknown',
      headline: 'No open trip for that number',
      detail: 'A lorry with no open trip has nothing to record a gate out against. Check the number, or look it up on the trips list.',
      devices: [],
    };
  }
  const outstanding = unreturnedDevices(trip, data);
  const names = outstanding.map((d) => d.identifier).join(', ');
  const plural = outstanding.length > 1;
  if (!outstanding.length) {
    return {
      state: 'clear',
      headline: 'Clear to leave',
      detail: 'Nothing of the plant’s is on this lorry. Record the gate out and lift the barrier.',
      devices: [],
    };
  }
  if (!journeyEndsHere(trip)) {
    return {
      state: 'travels',
      headline: 'Clear to leave',
      detail: `${names} ${plural ? 'travel' : 'travels'} with this load and ${plural ? 'are' : 'is'} due back after the delivery. This load is still being tracked, so ${plural ? 'they stay' : 'it stays'} on the lorry.`,
      devices: [],
    };
  }
  const ready = outstanding.filter((d) => d.collectable);
  if (ready.length) {
    return {
      state: 'collect',
      headline: plural ? 'Collect the trackers before this lorry leaves' : 'Collect the tracker before this lorry leaves',
      detail: `${names} ${plural ? 'belong' : 'belongs'} to the plant and this lorry is not coming back. Take ${plural ? 'them' : 'it'} off the vehicle and record ${plural ? 'them' : 'it'} here.`,
      devices: ready,
    };
  }
  return {
    state: 'warn',
    headline: plural ? 'This lorry is carrying plant trackers' : 'This lorry is carrying a plant tracker',
    detail: `${names} ${plural ? 'were' : 'was'} issued for this trip and ${plural ? 'have' : 'has'} to come back here. Record the gate out, which finishes the trip, then record ${plural ? 'them' : 'it'} as collected.`,
    devices: outstanding,
  };
};

/** Whether the barrier should go up without anything further being recorded. */
export const settled = (state) => state === 'clear' || state === 'travels';
